import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { resolveCuratedImage } from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import type { CampaignCardData } from 'volto-ministerio-publico-federal/components/UI';
import type { CampaignItem, CampaignRef } from './schema';

const IMAGE_SCALE = 'preview';

export interface CampaignBrain {
  '@id': string;
  UID?: string;
  title: string;
  description?: string;
  image_field?: string;
  image_scales?: Record<string, unknown[]>;
}

const targetOf = (item: CampaignItem): CampaignRef | undefined =>
  item.href?.[0]?.['@id'] ? item.href[0] : undefined;

const isPortalPage = (target: CampaignRef): boolean =>
  isInternalURL(target['@id'] as string);

const hasImage = (brain: CampaignBrain): boolean =>
  Boolean(brain.image_scales?.[brain.image_field || 'image']?.length);

const toPortalPageCard = (
  target: CampaignRef,
  brain: CampaignBrain,
  item: CampaignItem,
): CampaignCardData => ({
  key: target['@id'] as string,
  href: flattenToAppURL(target['@id'] as string),
  title: brain.title,
  description: brain.description,
  image: hasImage(brain) ? { item: brain } : null,
  alt: '',
  opensInNewTab: Boolean(item.external),
});

const toExternalSiteCard = (
  target: CampaignRef,
  item: CampaignItem,
): CampaignCardData => ({
  key: target['@id'] as string,
  href: target['@id'] as string,
  title: item.title as string,
  description: item.description,
  image: resolveCuratedImage(item.image, IMAGE_SCALE),
  alt: item.alt || '',
  opensInNewTab: item.external !== false,
});

export const getPortalCampaignUids = (items: CampaignItem[]): string[] =>
  items
    .map(targetOf)
    .filter((target): target is CampaignRef => Boolean(target))
    .filter(isPortalPage)
    .map((target) => target.UID)
    .filter((uid): uid is string => Boolean(uid));

/**
 * Página do portal: o card vem do brain, então acompanha a página e some com
 * ela. Site externo: não há o que consultar, e os campos do item são a fonte.
 */
export const toCampaignCards = (
  items: CampaignItem[],
  brains: CampaignBrain[],
): CampaignCardData[] => {
  const byUid = new Map(brains.map((brain) => [brain.UID, brain]));

  return items.reduce<CampaignCardData[]>((cards, item) => {
    const target = targetOf(item);

    if (!target) {
      return cards;
    }

    if (isPortalPage(target)) {
      const brain = target.UID ? byUid.get(target.UID) : undefined;

      return brain ? [...cards, toPortalPageCard(target, brain, item)] : cards;
    }

    return item.title ? [...cards, toExternalSiteCard(target, item)] : cards;
  }, []);
};
